import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import {
    AlertTriangle,
    CheckCircle,
    ChevronRight,
    FileText,
    Target,
    Zap,
    Shield,
    List,
    Sparkles,
    FileSpreadsheet,
    ClipboardCheck,
    Clock,
    GitBranch,
    ArrowUpRight,
    ArrowDownRight,
    Activity
} from 'lucide-react';

const Dashboard: React.FC = () => {
    const [expandedWorkflowStep, setExpandedWorkflowStep] = useState<number | null>(null);
    const {
        activeStandard,
        metrics,
        workflowSteps,
    } = useAppContext();

    // ============================================================
    // STAT CARD COMPONENT
    // ============================================================
    const StatCard: React.FC<{
        title: string;
        value: number | string;
        icon: React.ReactNode;
        color: string;
        subtitle?: string;
        trend?: { direction: 'up' | 'down'; value: string };
        link?: string;
    }> = ({ title, value, icon, color, subtitle, trend, link }) => {
        const content = (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:shadow-lg transition-all hover:border-purple-500/50">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-slate-400 mb-1">{title}</p>
                        <p className="text-3xl font-bold text-white">{value}</p>
                        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
                        {trend && (
                            <div className={`flex items-center gap-1 mt-2 text-sm ${trend.direction === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                                {trend.direction === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                                <span>{trend.value}</span>
                            </div>
                        )}
                    </div>
                    <div className={`p-3 rounded-xl ${color}`}>
                        {icon}
                    </div>
                </div>
            </div>
        );

        if (link) {
            return <Link to={link}>{content}</Link>;
        }
        return content;
    };

    return (
        <div className="p-6 space-y-6">
            {/* AI TOOLS - PROMINENT AT TOP */}
            <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Sparkles size={24} className="text-yellow-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold">🤖 AI-Assisted PFMEA Workflow</h2>
                        <p className="text-purple-200 text-sm">Use AI tools to accelerate your path to AIAG-VDA certification</p>
                    </div>
                </div>

                {/* AI Tools Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Magnus AI Chat */}
                    <Link
                        to="/ai-assistant"
                        className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 transition-all hover:scale-105 hover:shadow-xl group"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
                                <Sparkles size={20} className="text-white" />
                            </div>
                            <span className="font-bold text-lg">Magnus AI Chat</span>
                        </div>
                        <p className="text-purple-200 text-sm mb-2">Ask questions, get guidance, navigate the app</p>
                        <div className="border-t border-white/20 pt-2 mt-2">
                            <p className="text-xs text-purple-300">✓ Explain AIAG-VDA requirements</p>
                            <p className="text-xs text-purple-300">✓ Help identify failure modes</p>
                            <p className="text-xs text-purple-300">✓ Suggest prevention controls</p>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm mt-3 group-hover:translate-x-1 transition-transform">
                            <span>Start Chat</span>
                            <ChevronRight size={16} />
                        </div>
                    </Link>

                    {/* AI PFMEA Generator */}
                    <Link
                        to="/ideation"
                        className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 transition-all hover:scale-105 hover:shadow-xl group"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                                <FileSpreadsheet size={20} className="text-white" />
                            </div>
                            <span className="font-bold text-lg">AI PFMEA Generator</span>
                        </div>
                        <p className="text-purple-200 text-sm mb-2">Generate complete PFMEA from process description</p>
                        <div className="border-t border-white/20 pt-2 mt-2">
                            <p className="text-xs text-purple-300">✓ Generate Process Flow</p>
                            <p className="text-xs text-purple-300">✓ Create Failure Modes & Effects</p>
                            <p className="text-xs text-purple-300">✓ Suggest S/O/D ratings</p>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm mt-3 group-hover:translate-x-1 transition-transform">
                            <span>Generate PFMEA</span>
                            <ChevronRight size={16} />
                        </div>
                    </Link>

                    {/* AI Gap Closer */}
                    <Link
                        to="/control-plan"
                        className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 transition-all hover:scale-105 hover:shadow-xl group"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
                                <ClipboardCheck size={20} className="text-white" />
                            </div>
                            <span className="font-bold text-lg">Control Plan AI</span>
                        </div>
                        <p className="text-purple-200 text-sm mb-2">Auto-generate Control Plan from PFMEA</p>
                        <div className="border-t border-white/20 pt-2 mt-2">
                            <p className="text-xs text-purple-300">✓ Link CC/SC to controls</p>
                            <p className="text-xs text-purple-300">✓ Generate inspection methods</p>
                            <p className="text-xs text-purple-300">✓ Create reaction plans</p>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm mt-3 group-hover:translate-x-1 transition-transform">
                            <span>Generate Control Plan</span>
                            <ChevronRight size={16} />
                        </div>
                    </Link>

                    {/* Auditor AI */}
                    <Link
                        to="/compliance-auditor"
                        className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 transition-all hover:scale-105 hover:shadow-xl group relative overflow-hidden"
                    >
                        <div className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-black text-xs font-bold rounded-full">
                            NEW
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg">
                                <Shield size={20} className="text-white" />
                            </div>
                            <span className="font-bold text-lg">Auditor AI</span>
                        </div>
                        <p className="text-purple-200 text-sm mb-2">Pre-audit your PFMEA for PPAP readiness</p>
                        <div className="border-t border-white/20 pt-2 mt-2">
                            <p className="text-xs text-purple-300">✓ AIAG-VDA compliance check</p>
                            <p className="text-xs text-purple-300">✓ Critical Gap Detection</p>
                            <p className="text-xs text-purple-300">✓ Auditor Watchlist</p>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm mt-3 group-hover:translate-x-1 transition-transform">
                            <span>Run Audit</span>
                            <ChevronRight size={16} />
                        </div>
                    </Link>
                </div>
            </div>

            {/* Data Quality Overview */}
            <div className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-800 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <Shield size={24} />
                            PFMEA Data Quality Score
                        </h2>
                        <p className="text-purple-200 text-sm mt-1">Real-time verification of data completeness</p>
                    </div>
                    <div className="text-center">
                        <div className={`text-5xl font-bold ${metrics.dataQuality.overallCompleteness >= 80 ? 'text-green-400' :
                            metrics.dataQuality.overallCompleteness >= 50 ? 'text-amber-400' :
                                'text-red-400'
                            }`}>
                            {metrics.dataQuality.overallCompleteness}%
                        </div>
                        <p className="text-xs text-purple-300">Completeness</p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="h-3 bg-white/20 rounded-full overflow-hidden mb-4">
                    <div
                        className={`h-full rounded-full transition-all ${metrics.dataQuality.overallCompleteness >= 80 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                            metrics.dataQuality.overallCompleteness >= 50 ? 'bg-gradient-to-r from-amber-400 to-yellow-500' :
                                'bg-gradient-to-r from-red-400 to-rose-500'
                            }`}
                        style={{ width: `${metrics.dataQuality.overallCompleteness}%` }}
                    />
                </div>

                {/* Quality Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-purple-200">Complete Items</span>
                            <CheckCircle size={14} className="text-green-400" />
                        </div>
                        <p className="text-2xl font-bold mt-1">{metrics.dataQuality.completePfmeaItems}</p>
                        <p className="text-xs text-purple-300">of {metrics.totalPfmeaItems}</p>
                    </div>

                    <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-purple-200">DFMEA Links</span>
                            {metrics.dataQuality.itemsWithDfmeaLinks === 0 ?
                                <AlertTriangle size={14} className="text-red-400" /> :
                                <CheckCircle size={14} className="text-green-400" />
                            }
                        </div>
                        <p className={`text-2xl font-bold mt-1 ${metrics.dataQuality.itemsWithDfmeaLinks === 0 ? 'text-red-400' : ''}`}>
                            {metrics.dataQuality.itemsWithDfmeaLinks}
                        </p>
                        <p className="text-xs text-purple-300">{metrics.dataQuality.itemsWithDfmeaLinks === 0 ? 'None linked!' : 'linked'}</p>
                    </div>

                    <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-purple-200">Control Plan</span>
                            {metrics.dataQuality.itemsWithControlPlanLinks === 0 ?
                                <AlertTriangle size={14} className="text-red-400" /> :
                                <CheckCircle size={14} className="text-green-400" />
                            }
                        </div>
                        <p className={`text-2xl font-bold mt-1 ${metrics.dataQuality.itemsWithControlPlanLinks === 0 ? 'text-red-400' : ''}`}>
                            {metrics.dataQuality.itemsWithControlPlanLinks}
                        </p>
                        <p className="text-xs text-purple-300">{metrics.dataQuality.itemsWithControlPlanLinks === 0 ? 'None linked!' : 'linked'}</p>
                    </div>

                    <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-purple-200">Process Links</span>
                            {metrics.dataQuality.itemsWithProcessStepLinks === 0 ?
                                <AlertTriangle size={14} className="text-amber-400" /> :
                                <CheckCircle size={14} className="text-green-400" />
                            }
                        </div>
                        <p className={`text-2xl font-bold mt-1 ${metrics.dataQuality.itemsWithProcessStepLinks === 0 ? 'text-amber-400' : ''}`}>
                            {metrics.dataQuality.itemsWithProcessStepLinks}
                        </p>
                        <p className="text-xs text-purple-300">{metrics.dataQuality.itemsWithProcessStepLinks === 0 ? 'None linked!' : 'linked'}</p>
                    </div>

                    <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-purple-200">Missing Controls</span>
                            {metrics.dataQuality.missingControls > 0 ?
                                <AlertTriangle size={14} className="text-amber-400" /> :
                                <CheckCircle size={14} className="text-green-400" />
                            }
                        </div>
                        <p className={`text-2xl font-bold mt-1 ${metrics.dataQuality.missingControls > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                            {metrics.dataQuality.missingControls}
                        </p>
                        <p className="text-xs text-purple-300">{metrics.dataQuality.missingControls > 0 ? 'need controls' : 'all have controls'}</p>
                    </div>

                    <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-purple-200">High-Risk No Action</span>
                            {metrics.dataQuality.missingActions > 0 ?
                                <AlertTriangle size={14} className="text-red-400" /> :
                                <CheckCircle size={14} className="text-green-400" />
                            }
                        </div>
                        <p className={`text-2xl font-bold mt-1 ${metrics.dataQuality.missingActions > 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {metrics.dataQuality.missingActions}
                        </p>
                        <p className="text-xs text-purple-300">{metrics.dataQuality.missingActions > 0 ? 'need actions!' : 'all addressed'}</p>
                    </div>
                </div>

                {/* AUDITOR WATCHLIST (New Sensitive Warnings) */}
                {(metrics.dataQuality.safetyViolations! > 0 || metrics.dataQuality.orphanCriticals! > 0 || metrics.dataQuality.orphanedProcessSteps! > 0) && (
                    <div className="mt-4 bg-red-900/30 border border-red-500/50 rounded-2xl p-4 animate-pulse-slow">
                        <h3 className="text-red-400 font-bold flex items-center gap-2 mb-3">
                            <AlertTriangle size={20} />
                            AUDITOR RED FLAGS - IMMEDIATE ACTION REQUIRED
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Safety Violations */}
                            {metrics.dataQuality.safetyViolations! > 0 && (
                                <div className="bg-red-950/50 p-3 rounded-lg border border-red-500/30 flex items-start gap-3">
                                    <AlertTriangle className="text-red-500 mt-1 shrink-0" size={18} />
                                    <div>
                                        <p className="font-bold text-red-200">Safety Violation (S≥9)</p>
                                        <p className="text-sm text-red-300/80">
                                            {metrics.dataQuality.safetyViolations} Critical items have NO actions defined.
                                            This is an automatic audit failure.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Linkage Mismatches (Orphan Criticals) */}
                            {metrics.dataQuality.orphanCriticals! > 0 && (
                                <div className="bg-red-950/50 p-3 rounded-lg border border-red-500/30 flex items-start gap-3">
                                    <FileText className="text-red-500 mt-1 shrink-0" size={18} />
                                    <div>
                                        <p className="font-bold text-red-200">Critical Linkage Broken</p>
                                        <p className="text-sm text-red-300/80">
                                            {metrics.dataQuality.orphanCriticals} "CC" items in PFMEA are missing from Control Plan.
                                            Major non-conformance.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Orphaned Process Steps */}
                            {metrics.dataQuality.orphanedProcessSteps! > 0 && (
                                <div className="bg-amber-950/50 p-3 rounded-lg border border-amber-500/30 flex items-start gap-3">
                                    <List className="text-amber-500 mt-1 shrink-0" size={18} />
                                    <div>
                                        <p className="font-bold text-amber-200">Unanalyzed Process Steps</p>
                                        <p className="text-sm text-amber-300/80">
                                            {metrics.dataQuality.orphanedProcessSteps} steps in Flow Diagram have no PFMEA analysis.
                                            Risk assessment incomplete.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Traditional Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total PFMEA Items"
                    value={metrics.totalPfmeaItems}
                    icon={<FileText size={20} className="text-blue-500" />}
                    color="bg-blue-500/20"
                    trend={{ direction: 'up', value: '12% this week' }}
                />
                <StatCard
                    title="Average Severity"
                    value={metrics.averageSeverity}
                    icon={<Activity size={20} className="text-purple-500" />}
                    color="bg-purple-500/20"
                />
                <StatCard
                    title="Open Actions"
                    value={metrics.openActions}
                    icon={<Clock size={20} className="text-amber-500" />}
                    color="bg-amber-500/20"
                    subtitle={`${metrics.overdueActions} overdue`}
                />
                <StatCard
                    title="Control Plan Items"
                    value={metrics.controlPlanItems}
                    icon={<ClipboardCheck size={20} className="text-green-500" />}
                    color="bg-green-500/20"
                />
            </div>

            {/* AIAG-VDA 7-Step Workflow */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <GitBranch size={24} className="text-blue-400" />
                        AIAG-VDA 7-Step Progress
                    </h2>
                    <span className="text-sm text-slate-400">Project: {activeStandard}</span>
                </div>

                <div className="space-y-4">
                    {workflowSteps.map((step) => (
                        <div key={step.id} className="bg-slate-700/50 rounded-lg p-4 transition-all hover:bg-slate-700">
                            <div className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => setExpandedWorkflowStep(expandedWorkflowStep === step.id ? null : step.id)}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step.status === 'completed' ? 'bg-green-500 text-white' :
                                        step.status === 'failed' ? 'bg-red-500 text-white animate-pulse' :
                                            step.status === 'in-progress' ? 'bg-blue-500 text-white' :
                                                'bg-slate-600 text-slate-400'
                                        }`}>
                                        {step.id}
                                    </div>
                                    <div>
                                        <h3 className={`font-semibold ${step.status === 'failed' ? 'text-red-400' : 'text-white'}`}>{step.name}</h3>
                                        <p className="text-xs text-slate-400">{step.description}</p>
                                        {step.status === 'failed' && (
                                            <p className="text-xs text-red-400 font-medium mt-0.5">⚠️ Critical findings - Auditor intervention required</p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <span className={`text-sm font-bold ${step.status === 'completed' ? 'text-green-400' :
                                            step.status === 'failed' ? 'text-red-400' :
                                                step.completedPercentage > 0 ? 'text-blue-400' : 'text-slate-500'
                                            }`}>
                                            {step.completedPercentage}%
                                        </span>
                                    </div>
                                    <ChevronRight
                                        size={18}
                                        className={`text-slate-500 transition-transform ${expandedWorkflowStep === step.id ? 'rotate-90' : ''}`}
                                    />
                                </div>
                            </div>

                            {/* Detailed Progress Bar */}
                            <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full ${step.status === 'completed' ? 'bg-green-500' :
                                        step.status === 'failed' ? 'bg-red-500' :
                                            'bg-blue-500'
                                        }`}
                                    style={{ width: `${step.completedPercentage}%` }}
                                />
                            </div>

                            {/* Expanded Details */}
                            {expandedWorkflowStep === step.id && (
                                <div className="mt-4 pt-4 border-t border-slate-600 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Key Deliverables</h4>
                                            <ul className="space-y-1">
                                                <li className="text-sm text-slate-300 flex items-center gap-2">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${step.completedPercentage > 20 ? 'bg-green-400' : 'bg-slate-500'}`} />
                                                    Primary Document
                                                </li>
                                                <li className="text-sm text-slate-300 flex items-center gap-2">
                                                    <div className={`w-1.5 h-1.5 rounded-full ${step.completedPercentage > 60 ? 'bg-green-400' : 'bg-slate-500'}`} />
                                                    Review & Approval
                                                </li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Quick Actions</h4>
                                            <div className="flex flex-col gap-2">
                                                {step.linkedPages.map(page => (
                                                    <Link
                                                        key={page}
                                                        to={page}
                                                        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 hover:underline"
                                                    >
                                                        Go to {page.replace('/', '').replace('-', ' ')} <ChevronRight size={12} />
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom Grid: Action Priority & 4M Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Action Priority Distribution */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <Target size={24} className="text-red-400" />
                            Risk Distribution (AP)
                        </h2>
                        <span className="text-xs text-slate-400">
                            Total: {metrics.totalPfmeaItems} items
                        </span>
                    </div>

                    {/* Chart Container */}
                    <div className="flex items-end justify-around gap-6 h-48 px-4 pt-8">
                        {/* High Priority Bar */}
                        <div className="flex-1 flex flex-col items-center gap-2">
                            <span className="text-red-400 font-bold text-2xl mb-2">
                                {metrics.byActionPriority.high}
                            </span>
                            <div
                                className="w-full bg-gradient-to-t from-red-600 to-red-400 rounded-t-lg transition-all hover:from-red-500 hover:to-red-300 shadow-lg shadow-red-900/30"
                                style={{
                                    height: `${Math.max(10, (metrics.byActionPriority.high / Math.max(metrics.totalPfmeaItems, 1)) * 100)}%`,
                                    minHeight: metrics.byActionPriority.high > 0 ? '20px' : '8px'
                                }}
                            />
                            <span className="text-red-400 font-semibold text-sm">High</span>
                            <span className="text-xs text-slate-500">
                                {metrics.totalPfmeaItems > 0
                                    ? Math.round((metrics.byActionPriority.high / metrics.totalPfmeaItems) * 100)
                                    : 0}%
                            </span>
                        </div>

                        {/* Medium Priority Bar */}
                        <div className="flex-1 flex flex-col items-center gap-2">
                            <span className="text-amber-400 font-bold text-2xl mb-2">
                                {metrics.byActionPriority.medium}
                            </span>
                            <div
                                className="w-full bg-gradient-to-t from-amber-600 to-amber-400 rounded-t-lg transition-all hover:from-amber-500 hover:to-amber-300 shadow-lg shadow-amber-900/30"
                                style={{
                                    height: `${Math.max(10, (metrics.byActionPriority.medium / Math.max(metrics.totalPfmeaItems, 1)) * 100)}%`,
                                    minHeight: metrics.byActionPriority.medium > 0 ? '20px' : '8px'
                                }}
                            />
                            <span className="text-amber-400 font-semibold text-sm">Medium</span>
                            <span className="text-xs text-slate-500">
                                {metrics.totalPfmeaItems > 0
                                    ? Math.round((metrics.byActionPriority.medium / metrics.totalPfmeaItems) * 100)
                                    : 0}%
                            </span>
                        </div>

                        {/* Low Priority Bar */}
                        <div className="flex-1 flex flex-col items-center gap-2">
                            <span className="text-green-400 font-bold text-2xl mb-2">
                                {metrics.byActionPriority.low}
                            </span>
                            <div
                                className="w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg transition-all hover:from-green-500 hover:to-green-300 shadow-lg shadow-green-900/30"
                                style={{
                                    height: `${Math.max(10, (metrics.byActionPriority.low / Math.max(metrics.totalPfmeaItems, 1)) * 100)}%`,
                                    minHeight: metrics.byActionPriority.low > 0 ? '20px' : '8px'
                                }}
                            />
                            <span className="text-green-400 font-semibold text-sm">Low</span>
                            <span className="text-xs text-slate-500">
                                {metrics.totalPfmeaItems > 0
                                    ? Math.round((metrics.byActionPriority.low / metrics.totalPfmeaItems) * 100)
                                    : 0}%
                            </span>
                        </div>
                    </div>

                    {/* Legend/Summary */}
                    <div className="mt-4 pt-4 border-t border-slate-700 flex justify-center gap-6 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-red-500"></div> Action Required
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-amber-500"></div> Monitor
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-green-500"></div> Acceptable
                        </span>
                    </div>
                </div>

                {/* 4M Analysis */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <Zap size={24} className="text-yellow-400" />
                            Root Cause (4M)
                        </h2>
                    </div>
                    <div className="space-y-4">
                        {Object.entries(metrics.by4M).map(([category, count]) => (
                            <div key={category} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-300">{category}</span>
                                    <span className="text-slate-400">{count} items</span>
                                </div>
                                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-purple-500 rounded-full"
                                        style={{ width: `${(count / metrics.totalPfmeaItems) * 100 || 0}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
